import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../data/translations';
import { motion } from 'framer-motion';

export default function HeroSection() {
  const { lang } = useLanguage();
  const t = translations.hero[lang];

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-screen overflow-hidden">
      {/* Navy background with diagonal gold accent */}
      <div className="absolute inset-0 bg-enbi-navy" />

      {/* Diagonal gold stripe */}
      <div
        className="absolute top-0 right-0 w-[60%] h-full bg-enbi-gold/10"
        style={{ clipPath: 'polygon(30% 0, 100% 0, 100% 100%, 0% 100%)' }}
      />
      <div
        className="absolute top-0 right-0 w-[55%] h-full"
        style={{
          clipPath: 'polygon(35% 0, 100% 0, 100% 100%, 5% 100%)',
          background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(10,25,47,0.3) 100%)'
        }}
      />

      {/* Corporate image with diagonal mask */}
      <div
        className="absolute top-0 right-0 w-[50%] h-full hidden lg:block"
        style={{ clipPath: 'polygon(40% 0, 100% 0, 100% 100%, 10% 100%)' }}
      >
        <img
          src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=1200&q=80"
          alt="Digital Business"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-enbi-navy" />
      </div>

      {/* Gold accent line */}
      <div
        className="absolute bottom-0 left-0 w-full h-2 bg-enbi-gold"
        style={{ clipPath: 'polygon(0 0, 60% 0, 55% 100%, 0 100%)' }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 md:px-12 lg:px-24 min-h-screen flex items-center">
        <div className="max-w-2xl pt-20 pb-8">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <img src="/logo-full-white.png" alt="ENBI Marketing" className="h-10 sm:h-14 w-auto mb-6" />

            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-[2px] bg-enbi-gold" />
              <span className="text-enbi-gold text-xs tracking-[0.3em] uppercase font-semibold">
                Digital Media Agency
              </span>
            </div>

            <h1 className="font-outfit text-[1.65rem] sm:text-5xl lg:text-6xl font-bold text-white uppercase tracking-tighter leading-[1.05] mb-6 sm:mb-8">
              {t.headline}
            </h1>

            <p className="text-white/70 text-sm sm:text-lg leading-relaxed mb-8 sm:mb-12 max-w-xl">
              {t.sub}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={() => scrollTo('contact')}
                data-testid="hero-cta-primary"
                className="bg-enbi-gold text-enbi-navy px-6 sm:px-8 py-3.5 sm:py-4 text-[11px] sm:text-xs tracking-[0.15em] sm:tracking-[0.2em] uppercase font-bold hover:bg-enbi-gold-light transition-all duration-300 hover:scale-[1.02] text-center"
              >
                {t.cta}
              </button>
              <button
                onClick={() => scrollTo('services')}
                data-testid="hero-cta-secondary"
                className="border border-white/30 text-white px-6 sm:px-8 py-3.5 sm:py-4 text-[11px] sm:text-xs tracking-[0.15em] sm:tracking-[0.2em] uppercase font-semibold hover:border-enbi-gold hover:text-enbi-gold transition-all duration-300 text-center"
              >
                {t.ctaSecondary}
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom diagonal divider */}
      <div className="absolute bottom-0 left-0 w-full">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <polygon points="0,80 1440,80 1440,30 0,80" fill="#f8f9fa" />
        </svg>
      </div>
    </section>
  );
}
